import React from 'react';
import PropTypes from 'prop-types';
import * as ReactRedux from 'react-redux';
import { bindActionCreators } from 'redux';
import TextareaAutosize from 'react-textarea-autosize';
import ImageButton from 'ImageButton';
import WithHover from 'WithHover';
import * as SectionOrganizerActions from './SectionOrganizerActions';
import styles from '../../../sass/main.scss';

const DEFAULT_SECTION_NAME = 'SECTION 1';
const DEFAULT_HEIGHT = 14;
const SECTION_DIVIDER_EDITING_OFFSET_HEIGHT = 17;
const MAX_SECTION_DIVIDER_CONTENT_HEIGHT = 162;
const MAX_SECTION_DIVIDER_CONTENT_WIDTH = 242;
const EDITING_PADDING_OFFSET = 21;
const NOT_EDITING_PADDING_OFFSET = 13;

class SectionDivider extends React.PureComponent {
  // Since signature section deletion doesn't happen
  // in this component, the section divider name is not
  // updated to DEFAULT_SECTION_NAME when
  // a lone signature section is deleted
  // In this case, we have to take the new name
  // from the new section props
  static getDerivedStateFromProps(props, state) {
    return {
      ...state,
      name: !state.isEditing ? props.name : state.name,
    };
  }

  constructor(props) {
    super(props);

    this.onAddSection = this.onAddSection.bind(this);
    this.onEditSection = this.onEditSection.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onAddSection = this.onAddSection.bind(this);
    this.onDelete = this.onDelete.bind(this);

    this.getDividerContent = this.getDividerContent.bind(this);
    this.onSetExtraPadding = this.onSetExtraPadding.bind(this);
    this.isDeleteDisabled = this.isDeleteDisabled.bind(this);

    this.state = {
      isEditing: false,
      name: this.props.name,
      extraPadding: 0,
    };
  }

  onSetExtraPadding(extraPadding) {
    this.setState({
      extraPadding: extraPadding - DEFAULT_HEIGHT,
    });
  }

  onResize(resizeheight) {
    const height = Math.min(
      parseInt(resizeheight, 10),
      MAX_SECTION_DIVIDER_CONTENT_HEIGHT,
    ) || DEFAULT_HEIGHT;

    this.onSetExtraPadding(height + SECTION_DIVIDER_EDITING_OFFSET_HEIGHT);
  }

  onChange(e) {
    this.setState({
      isEditing: true,
      name: e.target.value,
    });
  }

  onEditSection() {
    if (this.props.isEditable) {
      this.setState({
        isEditing: true,
      });
    }
  }

  onDelete(e) {
    this.setState({
      isEditing: false,
    });
    this.props.removeSectionFromSectionOrganizer(
      this.props.sectionIndex,
    );

    this.props.onMouseOut();
    e.stopPropagation();
  }

  onAddSection(e) {
    const hasSectionName = this.state.name.length > 0;

    if (!hasSectionName) {
      return;
    }

    this.setState({
      isEditing: false,
    });

    if (this.props.isDone && hasSectionName) {
      this.props.renameSectionFromSectionOrganizer(
        this.state.name,
        this.props.sectionIndex,
      );
    } else if (hasSectionName) {
      this.props.splitSectionInSectionOrganizer(
        this.state.name,
        this.props.sectionIndex,
        this.props.fieldIndex,
      );
    }

    this.props.onMouseOut();
    e.stopPropagation();
  }

  getDividerContent() {
    if (this.state.isEditing) {
      const isDeleteDisabled = this.isDeleteDisabled();
      return (
        <div
          className="section-divider-content section-divider-editing-container"
        >
          <ImageButton
            alt="Delete"
            className="section-divider-delete-btn"
            src="/images/self-serve-builder/section-organizer/btn-delete-answer-2x.png"
            srcHover={
              isDeleteDisabled ?
                '/images/self-serve-builder/section-organizer/btn-delete-answer-2x.png' :
                '/images/self-serve-builder/section-organizer/btn-delete-answer-hover-2x.png'
            }
            onClick={this.onDelete}
            disabled={isDeleteDisabled}
            style={{
              cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
              opacity: isDeleteDisabled ? 0.5 : 1,
            }}
          />

          <TextareaAutosize
            className="organization-section-title"
            autoFocus
            rows={1}
            maxRows={9}
            onChange={this.state.isEditing ? this.onChange : null}
            onHeightChange={this.onResize}
            value={this.state.name}
          />

          <button
            className="section-divider-done-btn"
            style={{
              background: this.state.name.length > 0 ?
                styles.sectionOrganizerDividerEditDoneBtnEnabledColor :
                styles.sectionOrganizerDividerEditDoneBtnDisabledColor,
            }}
            onClick={this.onAddSection}
          >
            Done
          </button>
        </div>
      );
    } else if (this.props.isDone || this.props.isHover) {
      const label = this.state.name.length ? this.state.name : 'ADD SECTION';
      return (
        <button
          className="section-divider-content section-divider-edit-section-btn"
          style={{
            borderColor: this.props.isHover ?
              styles.sectionOrganizerDividerBorderHoverColor :
              styles.sectionOrganizerDividerBorderColor,
          }}
          onClick={this.onEditSection}
        >
          <TextareaAutosize
            className="organization-section-title"
            rows={1}
            cols={Math.min(label.length, MAX_SECTION_DIVIDER_CONTENT_WIDTH)}
            onChange={null}
            value={label}
            onHeightChange={this.onSetExtraPadding}
            style={{
              color: this.props.isHover ?
                styles.sectionOrganizerDividerTextHoverColor :
                styles.sectionOrganizerDividerTextColor,
              margin: this.props.isDone ? '0 15px 0 0' : 0,
            }}
          />

          {this.props.isDone && this.props.isEditable &&
            <img
              className="section-divider-edit-section-img"
              alt="Edit"
              src="/images/self-serve-builder/section-organizer/edit-section-2x.png"
            />
          }

        </button>
      );
    }

    return null;
  }

  isDeleteDisabled() {
    // only disable delete button if last section AND section name
    // equals our default
    // or this is a single-field section (signature)
    const hasDefaultName = this.state.name === DEFAULT_SECTION_NAME;
    return (hasDefaultName && this.props.isOnlySection) || !this.props.isMultiField;
  }

  render() {
    const PADDING_OFFSET = this.state.isEditing ?
      EDITING_PADDING_OFFSET :
      NOT_EDITING_PADDING_OFFSET;
    const padding = PADDING_OFFSET + (this.state.extraPadding / 2);
    let dividerClassName = this.props.isHover ? 'active-section-divider ' : '';
    dividerClassName += this.props.isDone ? 'finished-section-divider' : '';

    return (
      <div
        className="section-divider"
        onMouseOver={this.props.onMouseOver}
        onMouseLeave={this.props.onMouseOut}
        style={{
          paddingTop: padding,
          paddingBottom: padding,
        }}
      >
        <hr
          className={dividerClassName}
        />

        {this.getDividerContent()}

      </div>
    );
  }
}

SectionDivider.defaultProps = {
  fieldIndex: 0,
  isEditable: true,
  isDone: false,
  isHover: false,
  isMultiField: true,
  isOnlySection: false,
  name: '',
};

SectionDivider.propTypes = {
  fieldIndex: PropTypes.number,
  isEditable: PropTypes.bool,
  isDone: PropTypes.bool,
  isHover: PropTypes.bool,
  isMultiField: PropTypes.bool,
  isOnlySection: PropTypes.bool,
  name: PropTypes.string,
  onMouseOut: PropTypes.func.isRequired,
  onMouseOver: PropTypes.func.isRequired,
  removeSectionFromSectionOrganizer: PropTypes.func.isRequired,
  renameSectionFromSectionOrganizer: PropTypes.func.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  splitSectionInSectionOrganizer: PropTypes.func.isRequired,
};

const SectionDividerWithHover = WithHover(SectionDivider);

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    ...SectionOrganizerActions,
  }, dispatch)
);

export default ReactRedux.connect(
  null,
  mapDispatchToProps,
)(SectionDividerWithHover);



// WEBPACK FOOTER //
// ./src-react/SelfServePostBuilderSidePanel/SectionOrganizer/SectionDivider.jsx